export type BackendSource = "localhost" | "shared-dev";

const SKIP_LOCAL_AUTO_SWITCH_KEY = "m360.configuration-skip-local-auto-switch";
const LOCAL_UNAVAILABLE_NOTICE_KEY = "m360.backend-source.local-unavailable-notice";
const LOCAL_UNAVAILABLE_DETAIL_KEY = "m360.backend-source.local-unavailable-detail";

const isBackendSource = (value: unknown): value is BackendSource => value === "localhost" || value === "shared-dev";

export const BACKEND_SOURCE_QUERY_PARAM_NAMES = ["backend", "api-backend"] as const;

export function readBackendSourceQueryParam(search?: string): BackendSource | null {
  try {
    const rawSearch = search ?? (typeof window !== "undefined" ? (window.location?.search ?? "") : "");
    if (!rawSearch) return null;

    const params = new URLSearchParams(rawSearch);
    for (const name of BACKEND_SOURCE_QUERY_PARAM_NAMES) {
      const value = params.get(name);
      if (!value) continue;

      const normalized = value.trim().toLowerCase();
      if (normalized === "localhost" || normalized === "local") return "localhost";
      if (normalized === "shared-dev" || normalized === "shared" || normalized === "dev") return "shared-dev";
    }

    return null;
  } catch {
    return null;
  }
}

export function stripBackendSourceQueryParams(): void {
  try {
    if (typeof window === "undefined" || !window.location || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    let changed = false;
    for (const name of BACKEND_SOURCE_QUERY_PARAM_NAMES) {
      if (url.searchParams.has(name)) {
        url.searchParams.delete(name);
        changed = true;
      }
    }

    if (changed) {
      const search = url.searchParams.toString();
      window.history.replaceState(null, "", `${url.pathname}${search ? `?${search}` : ""}${url.hash}`);
    }
  } catch {
    // Best-effort; a leftover param still resolves via readBackendSourceQueryParam on next load.
  }
}

function getBackendSourceStorageKey(): string {
  const surface = import.meta.env.MODE.startsWith("portal") ? "portal" : "staff";
  return `m360.backend-source.${surface}`;
}

function getSessionLocalhostKey(): string {
  return `${getBackendSourceStorageKey()}.session-localhost`;
}

function getSessionSharedDevKey(): string {
  return `${getBackendSourceStorageKey()}.session-shared-dev`;
}

function discardDurableLocalhostPreference(): void {
  try {
    const stored = window.localStorage.getItem(getBackendSourceStorageKey());
    if (!stored) return;

    const parsed: unknown = JSON.parse(stored);
    if (parsed === "localhost") {
      window.localStorage.removeItem(getBackendSourceStorageKey());
    }
  } catch {
    // Best-effort migration of sticky localhost from older sessions.
  }
}

function readDurableBackendSource(): BackendSource | null {
  discardDurableLocalhostPreference();

  try {
    const stored = window.localStorage.getItem(getBackendSourceStorageKey());
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    return isBackendSource(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function hasSessionLocalhost(): boolean {
  try {
    return window.sessionStorage.getItem(getSessionLocalhostKey()) === "1";
  } catch {
    return false;
  }
}

function hasSessionSharedDev(): boolean {
  try {
    return window.sessionStorage.getItem(getSessionSharedDevKey()) === "1";
  } catch {
    return false;
  }
}

export function readBackendSource(): BackendSource | null {
  if (hasSessionLocalhost()) return "localhost";
  if (hasSessionSharedDev()) return "shared-dev";
  return readDurableBackendSource();
}

export function persistSessionSharedDevOverride(): void {
  try {
    window.sessionStorage.setItem(getSessionSharedDevKey(), "1");
    window.sessionStorage.removeItem(getSessionLocalhostKey());
  } catch {
    // Preference is best-effort; on failure consume keeps the live query param.
  }
}

export function persistBackendSource(source: BackendSource): void {
  try {
    if (source === "localhost") {
      window.sessionStorage.setItem(getSessionLocalhostKey(), "1");
      window.sessionStorage.removeItem(getSessionSharedDevKey());
      discardDurableLocalhostPreference();
      return;
    }

    window.sessionStorage.removeItem(getSessionLocalhostKey());
    window.sessionStorage.removeItem(getSessionSharedDevKey());
    window.localStorage.setItem(getBackendSourceStorageKey(), JSON.stringify(source));
  } catch {
    // Preference is best-effort; a failed write leaves the prior stored source in place after reload.
  }
}

export function hasLocalBackendBootstrapFailure(): boolean {
  try {
    return window.sessionStorage.getItem(SKIP_LOCAL_AUTO_SWITCH_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberLocalBackendBootstrapFailure(): void {
  try {
    window.sessionStorage.setItem(SKIP_LOCAL_AUTO_SWITCH_KEY, "1");
  } catch {
    // Best-effort; a failed write still lets Configuration auto-switch back to localhost.
  }
}

export function clearLocalBackendBootstrapFailure(): void {
  try {
    window.sessionStorage.removeItem(SKIP_LOCAL_AUTO_SWITCH_KEY);
  } catch {
    // Best-effort.
  }
}

export function hasLocalBackendUnavailableNotice(): boolean {
  try {
    return window.sessionStorage.getItem(LOCAL_UNAVAILABLE_NOTICE_KEY) === "1";
  } catch {
    return false;
  }
}

type LocalBackendUnavailableNoticeListener = () => void;

const localBackendUnavailableNoticeListeners = new Set<LocalBackendUnavailableNoticeListener>();

function notifyLocalBackendUnavailableNoticeListeners(): void {
  for (const listener of localBackendUnavailableNoticeListeners) {
    listener();
  }
}

export function subscribeLocalBackendUnavailableNotice(listener: LocalBackendUnavailableNoticeListener): () => void {
  localBackendUnavailableNoticeListeners.add(listener);
  return () => {
    localBackendUnavailableNoticeListeners.delete(listener);
  };
}

export function rememberLocalBackendUnavailableNotice(detail?: string): void {
  try {
    window.sessionStorage.setItem(LOCAL_UNAVAILABLE_NOTICE_KEY, "1");
    if (detail) {
      window.sessionStorage.setItem(LOCAL_UNAVAILABLE_DETAIL_KEY, detail);
    } else {
      window.sessionStorage.removeItem(LOCAL_UNAVAILABLE_DETAIL_KEY);
    }
  } catch {
    // Best-effort; missing notice still leaves the user on shared-dev.
  }
  notifyLocalBackendUnavailableNoticeListeners();
}

export function readLocalBackendUnavailableDetail(): string | null {
  try {
    return window.sessionStorage.getItem(LOCAL_UNAVAILABLE_DETAIL_KEY);
  } catch {
    return null;
  }
}

export function clearLocalBackendUnavailableNotice(): void {
  try {
    window.sessionStorage.removeItem(LOCAL_UNAVAILABLE_NOTICE_KEY);
    window.sessionStorage.removeItem(LOCAL_UNAVAILABLE_DETAIL_KEY);
  } catch {
    // Best-effort.
  }
  notifyLocalBackendUnavailableNoticeListeners();
}

export function applyBackendSource(source: BackendSource): void {
  persistBackendSource(source);
  if (source === "localhost") {
    clearLocalBackendBootstrapFailure();
    clearLocalBackendUnavailableNotice();
  }
  stripBackendSourceQueryParams();
  window.location.reload();
}

export function fallBackToSharedDevBackend(error?: Error): void {
  rememberLocalBackendBootstrapFailure();
  rememberLocalBackendUnavailableNotice(error?.message);
  applyBackendSource("shared-dev");
}
